import { BaseApiClient } from './base-client';

/**
 * Admin API - методы для админ-панели
 */
export class AdminApi extends BaseApiClient {

  // ===== ADMIN AUTH =====

  /**
   * Check if current user is super admin
   */
  async checkAdmin(): Promise<any> {
    const response = await this.client.get('/api/admin/check');
    return response.data;
  }

  // ===== STATS =====

  /**
   * Get admin dashboard overview stats
   */
  async getStatsOverview(): Promise<any> {
    const response = await this.client.get('/api/admin/stats/overview');
    return response.data;
  }

  /**
   * Get active users by period
   */
  async getActiveUsers(period: string, limit: number = 50): Promise<any> {
    const response = await this.client.get('/api/admin/stats/active-users', {
      params: { period, limit },
    });
    return response.data;
  }

  /**
   * Get server stats (CPU, memory, disk, etc.)
   */
  async getServerStats(): Promise<any> {
    const response = await this.client.get('/api/admin/server-stats');
    return response.data;
  }

  // ===== USER MANAGEMENT =====

  /**
   * Get user stats by telegramId
   */
  async getUserStats(telegramId: string): Promise<any> {
    const response = await this.client.get(`/api/admin/users/${telegramId}/stats`);
    return response.data;
  }

  /**
   * Add tokens to user
   */
  async addUserTokens(telegramId: string, tokens: number): Promise<any> {
    const response = await this.client.post(`/api/admin/users/${telegramId}/add-tokens`, { tokens });
    return response.data;
  }

  /**
   * Grant subscription to user
   */
  async grantSubscription(telegramId: string, tier: string): Promise<any> {
    const response = await this.client.post(`/api/admin/users/${telegramId}/grant-subscription`, { tier });
    return response.data;
  }

  /**
   * Cancel/remove user subscription
   */
  async cancelSubscription(telegramId: string): Promise<any> {
    const response = await this.client.post(`/api/admin/users/${telegramId}/cancel-subscription`, {});
    return response.data;
  }

  /**
   * Delete user and all their data
   */
  async deleteUser(telegramId: string): Promise<any> {
    const response = await this.client.delete(`/api/admin/users/${telegramId}`);
    return response.data;
  }

  // ===== BONUS POOL (Free Generations) =====

  /**
   * Get current bonus pool snapshot + audit history for a user.
   */
  async getUserBonusPool(telegramId: string): Promise<any> {
    const response = await this.client.get(`/api/admin/users/${telegramId}/bonus-pool`);
    return response.data;
  }

  /**
   * Mutate user's bonus pool. action: 'adjust' | 'reset' | 'revoke' | 'grant'.
   * payload depends on action — see backend.
   */
  async mutateUserBonusPool(
    telegramId: string,
    action: 'adjust' | 'reset' | 'revoke' | 'grant',
    payload: { poolSize?: number; usedCount?: number; expiresAt?: string; reason?: string } = {}
  ): Promise<any> {
    const response = await this.client.post(`/api/admin/users/${telegramId}/bonus-pool`, { action, payload });
    return response.data;
  }

  // ===== CAMPAIGNS =====

  /**
   * Get all campaigns
   */
  async getCampaigns(includeInactive: boolean = true, campaignType?: string): Promise<any> {
    const params: any = { includeInactive };
    if (campaignType) {
      params.campaignType = campaignType;
    }
    const response = await this.client.get('/api/admin/campaigns', {
      params,
    });
    return response.data;
  }

  /**
   * Get campaign details with analytics
   */
  async getCampaign(campaignId: string): Promise<any> {
    const response = await this.client.get(`/api/admin/campaigns/${campaignId}`);
    return response.data;
  }

  /**
   * Get campaign users
   */
  async getCampaignUsers(campaignId: string, limit: number = 50): Promise<any> {
    const response = await this.client.get(`/api/admin/campaigns/${campaignId}/users`, {
      params: { limit },
    });
    return response.data;
  }

  /**
   * Create campaign (FormData with optional avatar file)
   */
  async createCampaign(formData: FormData): Promise<any> {
    const response = await this.client.post('/api/admin/campaigns', formData);
    return response.data;
  }

  /**
   * Update campaign (FormData with optional avatar file)
   */
  async updateCampaign(campaignId: string, formData: FormData): Promise<any> {
    const response = await this.client.put(`/api/admin/campaigns/${campaignId}`, formData);
    return response.data;
  }

  /**
   * Toggle campaign active status
   */
  async toggleCampaignActive(campaignId: string, isActive: boolean): Promise<any> {
    const response = await this.client.put(`/api/admin/campaigns/${campaignId}`, { isActive });
    return response.data;
  }

  /**
   * Delete campaign
   */
  async deleteCampaign(campaignId: string): Promise<any> {
    const response = await this.client.delete(`/api/admin/campaigns/${campaignId}`);
    return response.data;
  }

  // ===== EMOJI PRESETS =====

  /**
   * Get all emoji presets
   */
  async getEmojiPresets(): Promise<any> {
    const response = await this.client.get('/api/admin/campaigns/emoji-presets/list');
    return response.data;
  }

  /**
   * Create emoji preset (FormData with image)
   */
  async createEmojiPreset(formData: FormData): Promise<any> {
    const response = await this.client.post('/api/admin/campaigns/emoji-presets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  /**
   * Delete emoji preset
   */
  async deleteEmojiPreset(id: number): Promise<any> {
    const response = await this.client.delete(`/api/admin/campaigns/emoji-presets/${id}`);
    return response.data;
  }

  // ===== BROADCAST =====

  /**
   * Send message to user(s) — accepts FormData (with media) or plain object (text-only)
   */
  async sendMessage(data: FormData | Record<string, any>): Promise<any> {
    const response = await this.client.post('/api/admin/send-message', data);
    return response.data;
  }

  /**
   * Create broadcast to all users — accepts FormData (with media) or plain object (text-only)
   */
  async createBroadcast(data: FormData | Record<string, any>): Promise<any> {
    const response = await this.client.post('/api/admin/broadcast', data);
    return response.data;
  }

  /**
   * Stop active broadcast
   */
  async stopBroadcast(broadcastId: string): Promise<any> {
    const response = await this.client.post(`/api/admin/broadcast/stop/${broadcastId}`);
    return response.data;
  }

  /**
   * Get broadcast status
   */
  async getBroadcastStatus(broadcastId: string): Promise<any> {
    const response = await this.client.get(`/api/admin/broadcast/status/${broadcastId}`);
    return response.data;
  }

  /**
   * Recall (delete) all messages from a broadcast
   */
  async recallBroadcast(broadcastId: string): Promise<any> {
    const response = await this.client.post(`/api/admin/broadcast/recall/${broadcastId}`);
    return response.data;
  }

  /**
   * Get recall info for a broadcast (can we recall, how many messages, time remaining)
   */
  async getRecallInfo(broadcastId: string): Promise<any> {
    const response = await this.client.get(`/api/admin/broadcast/recall-info/${broadcastId}`);
    return response.data;
  }

  /**
   * Pause active broadcast
   */
  async pauseBroadcast(broadcastId: string): Promise<any> {
    const response = await this.client.post(`/api/admin/broadcast/pause/${broadcastId}`);
    return response.data;
  }

  /**
   * Resume paused broadcast
   */
  async resumeBroadcast(broadcastId: string): Promise<any> {
    const response = await this.client.post(`/api/admin/broadcast/resume/${broadcastId}`);
    return response.data;
  }

  /**
   * Get broadcast history list
   */
  async getBroadcastList(type?: string, limit: number = 50, offset: number = 0): Promise<any> {
    const response = await this.client.get('/api/admin/broadcast/list', {
      params: { type, limit, offset },
    });
    return response.data;
  }

  /**
   * Delete a broadcast from history
   */
  async deleteBroadcast(broadcastId: string): Promise<any> {
    const response = await this.client.delete(`/api/admin/broadcast/${broadcastId}`);
    return response.data;
  }

  // ===== NOTIFICATIONS =====

  /**
   * Send in-app notification (FormData with optional image)
   */
  async sendInAppNotification(formData: FormData): Promise<any> {
    const response = await this.client.post('/api/admin/send-in-app-notification', formData);
    return response.data;
  }

  /**
   * Upload a single image used inline inside the rich-text editor of an
   * in-app broadcast. Returns { url, s3Key } so the editor can insert
   * <img src=url />. Separate endpoint from sendInAppNotification because
   * inline images live inside the description HTML built at edit time.
   */
  async uploadInlineMedia(file: File): Promise<{ success: boolean; url: string; s3Key: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post('/api/admin/upload-inline-media', formData);
    return response.data;
  }

  // ===== SUBSCRIPTION CONTENT =====

  /**
   * Get subscription content for tier
   */
  async getSubscriptionContent(tier: string): Promise<any> {
    const response = await this.client.get(`/api/admin/subscription-content/${tier}`);
    return response.data;
  }

  /**
   * Update subscription content for tier
   */
  async updateSubscriptionContent(tier: string, data: any): Promise<any> {
    const response = await this.client.put(`/api/admin/subscription-content/${tier}`, data);
    return response.data;
  }

  // ===== ANALYTICS =====

  /**
   * Get analytics overview (generations + revenue) for a date range
   */
  async getAnalyticsOverview(from: string, to: string): Promise<any> {
    const response = await this.client.get('/api/admin/analytics/overview', {
      params: { from, to },
    });
    return response.data;
  }

  /**
   * Get excluded users list
   */
  async getExcludedUsers(): Promise<any> {
    const response = await this.client.get('/api/admin/analytics/excluded-users');
    return response.data;
  }

  /**
   * Add user to exclusion list
   */
  async addExcludedUser(telegramId: string, note?: string): Promise<any> {
    const response = await this.client.post('/api/admin/analytics/excluded-users', { telegramId, note });
    return response.data;
  }

  /**
   * Remove user from exclusion list
   */
  async removeExcludedUser(telegramId: string): Promise<any> {
    const response = await this.client.delete(`/api/admin/analytics/excluded-users/${telegramId}`);
    return response.data;
  }

  /**
   * Get active users with detailed stats for analytics period
   */
  async getAnalyticsActiveUsers(from: string, to: string, limit: number = 50): Promise<any> {
    const response = await this.client.get('/api/admin/analytics/active-users', {
      params: { from, to, limit },
    });
    return response.data;
  }

  /**
   * Get per-user analytics (generations + chat) for a date range
   */
  async getAnalyticsUser(userId: string, from: string, to: string): Promise<any> {
    const response = await this.client.get(`/api/admin/analytics/user/${userId}`, {
      params: { from, to },
    });
    return response.data;
  }

  /**
   * AI analytics chat (Gemini 3.1 Pro)
   */
  async analyticsAiChat(
    message: string,
    period: { from: string; to: string },
    dataSources: string[],
    history: Array<{ role: string; content: string }>
  ): Promise<any> {
    const response = await this.client.post('/api/admin/analytics/ai-chat', {
      message, period, dataSources, history,
    });
    return response.data;
  }

  /**
   * Export analytics as XLSX file (returns blob)
   */
  async exportAnalytics(from: string, to: string): Promise<Blob> {
    const response = await this.client.get('/api/admin/analytics/export', {
      params: { from, to },
      responseType: 'blob',
    });
    return response.data;
  }

  // ===== TEAM MANAGEMENT =====

  async getTeams(search?: string, limit = 50, offset = 0): Promise<any> {
    const response = await this.client.get('/api/admin/teams', {
      params: { search, limit, offset }
    });
    return response.data;
  }

  async getTeamDetails(teamId: string): Promise<any> {
    const response = await this.client.get(`/api/admin/teams/${teamId}`);
    return response.data;
  }

  async getUserTeams(telegramId: string): Promise<any> {
    const response = await this.client.get(`/api/admin/users/${telegramId}/teams`);
    return response.data;
  }

  async addTeamTokens(teamId: string, amount: number, reason?: string): Promise<any> {
    const response = await this.client.post(`/api/admin/teams/${teamId}/add-tokens`, { amount, reason });
    return response.data;
  }

  async updateTeamSubscription(teamId: string, data: { seats?: number; period?: string; expiresAt?: string }): Promise<any> {
    const response = await this.client.put(`/api/admin/teams/${teamId}/subscription`, data);
    return response.data;
  }

  async deleteTeamAdmin(teamId: string): Promise<any> {
    const response = await this.client.delete(`/api/admin/teams/${teamId}`);
    return response.data;
  }
}
